/**
 * Physical Therapy Discharge Visit Note — options + empty payload.
 * After shared common header: Reason → Subjective → Status table →
 * Functional → Balance → Strength/ROM → Pain → Goals → Interventions →
 * HEP → Education → Discharge Plan → Communication → Summary (per screenshots).
 * No wound / nursing vitals sections. Pattern matches PT Visit Note.
 */

import { emptyPtNotesCommonHeader } from "./ptNotesCommonHeaderUtils";
import { PAIN_LEVELS } from "./ptClinicalVitals";

export { PAIN_LEVELS };

export const YES_NO_OPTIONS = ["Yes", "No"];

export const YES_NO_NOT_REQUIRED_OPTIONS = ["Yes", "No", "Not Required"];

export const DISCHARGE_REASON_OPTIONS = [
  "Goals Met",
  "Patient Refused",
  "Hospitalized",
  "Moved Out of Area",
  "Deceased",
  "Physician Request",
  "Insurance Limit",
  "Other",
];

export const DISCHARGE_STATUS_PARAMETER_OPTIONS = [
  "Strength (MMT - LE)",
  "Balance (BBS)",
  "Functional Mobility",
];

export const BED_MOBILITY_OPTIONS = [
  "Independent",
  "Mod. Independent",
  "Requires Assist",
  "Unable",
];

export const TRANSFER_ASSIST_OPTIONS = [
  "Independent",
  "Mod. Independent",
  "Contact Guard",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
];

export const TRANSFER_TYPE_OPTIONS = ["Bed", "Chair", "Toilet", "Shower"];

export const BALANCE_STATUS_OPTIONS = [
  "Improved",
  "Remained Same",
  "Declined",
];

export const FALL_RISK_OPTIONS = ["Low", "Moderate", "High"];

export const ROM_STATUS_OPTIONS = ["Within Functional Limits", "Limited"];

export const THERAPY_GOAL_ROWS = [
  "Mobility Goals",
  "Transfer Goals",
  "Strength Goals",
  "Balance Goals",
  "Safety Goals",
];

export const GOAL_STATUS_OPTIONS = ["Met", "Partially Met", "Not Met"];

export const PT_INTERVENTION_OPTIONS = [
  "Therapeutic Exercise",
  "Transfer Training",
  "Gait Training",
  "Balance Training",
  "Strengthening Program",
  "Home Exercise Program Training",
  "Fall Prevention Education",
  "Safety Education",
  "Caregiver Training",
];

export const HEP_UNDERSTANDING_OPTIONS = [
  "Verbalized Understanding",
  "Demonstrated Understanding",
  "Needs Reinforcement",
];

export const EDUCATION_TOPIC_OPTIONS = [
  "Medication Safety",
  "Fall Prevention",
  "Home Safety",
  "Mobility Techniques",
  "Exercise Program",
  "Disease Management",
];

export const LEARNER_OPTIONS = ["Patient", "Family / Caregiver"];

export const DISCHARGED_TO_OPTIONS = [
  "Home Independent",
  "Home With Caregiver Support",
  "Outpatient Therapy",
  "Skilled Nursing Facility",
  "Hospital",
  "Other",
];

export const FOLLOW_UP_OPTIONS = [
  "Continue Home Exercise Program",
  "Follow Up With Physician",
  "Outpatient PT Recommended",
  "No Further Therapy Required",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyStatusComparisonRow = (parameter) => ({
  parameter,
  initialBaseline: "",
  dischargeCurrent: "",
  status: "",
});

export const emptyStatusComparisonRows = () =>
  DISCHARGE_STATUS_PARAMETER_OPTIONS.map((parameter) =>
    emptyStatusComparisonRow(parameter),
  );

export const emptyGoalStatuses = () =>
  Object.fromEntries(THERAPY_GOAL_ROWS.map((goal) => [goal, ""]));

/** Full editable PT Discharge Note payload (JSON on dedicated row). */
export const emptyPtDischargePayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  dischargeDate: "",
  socDate: "",
  commonHeader: emptyPtNotesCommonHeader(),

  // —— 7. Reason for Discharge ——
  dischargeReason: "",
  dischargeReasonOther: "",

  // —— 8. Subjective Report ——
  subjectiveReport: "",

  // —— 9. Patient Status at Discharge ——
  statusComparison: emptyStatusComparisonRows(),

  // —— 10. Functional Assessment ——
  bedMobility: "",
  bedMobilityComments: "",
  transferAssistLevel: "",
  transferTypes: [],
  transferComments: "",

  // —— 11. Balance Assessment ——
  balanceStatus: "",
  fallRisk: "",
  fallPreventionEducation: "",
  balanceComments: "",

  // —— 12. Strength / ROM Assessment ——
  muscleStrengthFindings: "",
  romStatus: "",
  romDetails: "",

  // —— 13. Pain Assessment ——
  painLevel: null,
  painLocation: "",
  painEducationProvided: "",

  // —— 14. Therapy Goals Status ——
  goalStatuses: emptyGoalStatuses(),

  // —— 15. Skilled PT Interventions ——
  interventionsCompleted: [],

  // —— 16. Home Exercise Program ——
  hepProvided: "",
  hepUnderstanding: [],
  hepInstructions: "",

  // —— 17. Patient/Caregiver Education ——
  educationTopics: [],
  learners: [],
  educationNarrative: "",

  // —— 18. Discharge Plan ——
  dischargedTo: "",
  dischargedToOther: "",
  followUpRecommendations: [],
  additionalInstructions: "",

  // —— 19. Physician / Care Team Communication ——
  physicianNotified: "",
  rnCaseManagerNotified: "",
  communicationNotes: "",

  // —— 20. PT Discharge Summary Narrative ——
  dischargeSummary: "",
});

export const PT_DISCHARGE_ARRAY_KEYS = [
  "transferTypes",
  "interventionsCompleted",
  "hepUnderstanding",
  "educationTopics",
  "learners",
  "followUpRecommendations",
];
